import type { CSSProperties, ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { normalizeHex } from '../thumbs/palettes';
import s from './LandingPreview.module.css';

/** A variant's theme colours (background, ink, accent). */
export type LandingPalette = { bg: string; ink: string; acc: string };

/** The design's demo variant palette (VC.B) for a variant that has none yet. */
const FALLBACK: LandingPalette = { bg: '#FBF8F3', ink: '#2A1F17', acc: '#9C4A2F' };

/** Colours are data: only valid hex reaches the style (anything else falls back). */
export function landingVars(p: LandingPalette | null | undefined): CSSProperties {
  const bg = normalizeHex(p?.bg) ?? FALLBACK.bg;
  const ink = normalizeHex(p?.ink) ?? FALLBACK.ink;
  const acc = normalizeHex(p?.acc) ?? FALLBACK.acc;
  return { '--lp-bg': bg, '--lp-ink': ink, '--lp-acc': acc } as CSSProperties;
}

type MiniProps = {
  palette: LandingPalette | null | undefined;
  /** the short brand in the nav («Sharq») */
  brand: string;
  /** the hero headline in Instrument Serif */
  headline: string;
  /** the QA renderer's screenshot: shown instead of the mock once it exists */
  screenshotUrl?: string | null;
  /** overlays (the running shimmer, the failure) */
  children?: ReactNode;
  className?: string;
};

/**
 * `LandingPreview` (DESIGN_SPEC §3), mini: the variant cards' picture — the variant palette, a serif
 * hero with an accent button and a hatched image slot; the real screenshot once the build has one.
 */
export function LandingMini({ palette, brand, headline, screenshotUrl, children, className }: MiniProps) {
  return (
    <div className={cx(s.mini, className)} style={landingVars(palette)}>
      {screenshotUrl ? (
        <img className={s.shot} src={screenshotUrl} alt="" loading="lazy" />
      ) : (
        // The mock is decorative; the overlays (progress, the failure) stay readable.
        <>
          <div className={s.miniNav} aria-hidden>
            <span className={s.miniBrand}>{brand}</span>
            <div className={s.miniLinks}>
              <span />
              <span />
              <span />
            </div>
          </div>
          <div className={s.miniBody} aria-hidden>
            <div className={s.miniCopy}>
              <span className={s.miniTitle}>{headline}</span>
              <span className={s.miniCta} />
            </div>
            <div className={s.miniImage} />
          </div>
        </>
      )}
      {children}
    </div>
  );
}
